import { Project } from '@prisma/client';
import { useQueryClient } from '@tanstack/react-query';
import { MemberWithUser } from '@zond/api-interfaces';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  IconPicker,
  Input,
  Label,
  MultiSelect,
  Textarea,
} from '@zond/ui';
import { Loader2, Shuffle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useGetMembers } from '../../../members/members.queries';
import { useCreateProject, useGetProjects, useUpdateProject } from '../../projects.queries';

// Get all available icons
const availableIcons = Object.keys(LucideIcons).filter(
  (name) => name !== 'createLucideIcon' && name !== 'default'
);

interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  currentRow?: Project & {
    UserProject?: Array<{
      user: {
        id: string;
      };
    }>;
  };
}

export function ProjectFormDialog({ open, onOpenChange, mode, currentRow }: ProjectFormDialogProps) {
  const { t } = useTranslation('projects');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string | undefined>(undefined);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const { data: members, refetch, isLoading } = useGetMembers();
  const { data: projects } = useGetProjects();
  const queryClient = useQueryClient();

  const acceptedMembers = (members?.filter((member) => member.status === 'Active') || []) as MemberWithUser[];

  useEffect(() => {
    if (mode === 'edit' && currentRow) {
      setName(currentRow.name);
      setDescription(currentRow.description || '');
      setIcon(currentRow.icon || undefined);
      // Set selected users from UserProject relation
      const userIds = currentRow.UserProject?.map((up) => up.user.id.toString()) || [];
      setSelectedUserIds(new Set(userIds));
    } else {
      setName('');
      setDescription('');
      setIcon(undefined);
      setSelectedUserIds(new Set());
      if (mode === 'create' && open) {
        pickRandomIcon();
      }
    }
  }, [mode, currentRow, open]);

  const pickRandomIcon = () => {
    const randomIndex = Math.floor(Math.random() * availableIcons.length);
    setIcon(availableIcons[randomIndex]);
  };

  const createProject = useCreateProject(
    () => {
      toast.success(t('form.toast.created'));
      onOpenChange(false);
    },
    () => {
      toast.error(t('form.toast.createError'));
    }
  );

  const renameProject = useUpdateProject(
    () => {
      toast.success(t('form.toast.updated'));
      onOpenChange(false);
    },
    () => {
      toast.error(t('form.toast.updateError'));
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'create') {
      createProject.mutate({
        name,
        description,
        icon: icon || '',
        userIds: Array.from(selectedUserIds).map((id) => parseInt(id)),
      });
    } else if (currentRow) {
      renameProject.mutate({
        projectId: currentRow.id,
        name,
        description,
        icon: icon || '',
        userIds: Array.from(selectedUserIds).map((id) => parseInt(id)),
      });
    }
  };

  const isPending = mode === 'create' ? createProject.isPending : renameProject.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('actions.createProject') : t('actions.editProject')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' ? t('form.createDescription') : t('form.editDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('form.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('form.namePlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="icon">{t('form.iconLabel')}</Label>
            <div className="flex">
              <IconPicker value={icon} onChange={setIcon} placeholder={t('form.iconPlaceholder')} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="users">{t('form.membersLabel')}</Label>
            <div className="w-full">
              <MultiSelect
                title={t('form.addMembers')}
                placeholder={t('form.searchMembers')}
                clearText={t('form.removeAllMembers')}
                options={acceptedMembers
                  .filter((member) => member?.user?.email && member?.userId)
                  .map((member) => ({
                    label: member.user!.email!,
                    value: member.userId!.toString(),
                  }))}
                selectedValues={selectedUserIds}
                onSelectedValuesChange={(values: string[]) => {
                  const newSet = new Set(values.filter(Boolean));
                  setSelectedUserIds(newSet);
                }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('common:labels.description')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('form.descriptionPlaceholder')}
            />
          </div>
          <Button type="submit" className="w-full" disabled={!name.trim() || isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? t('actions.createProject') : t('common:actions.saveChanges')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
